import React, { useEffect, useContext, useState, useMemo, Dispatch, SetStateAction } from 'react';
import MultiFilter, { StyledChip } from "../MultiFilter";
import { Expert, EntityResponse, SelectedFilters, BasicExpert, CampaignService } from "../../types";
import { LocalizationContext } from "../LanguageContext/LocalizationContext";
import { AutocompleteGetTagProps } from "@material-ui/lab";
import { getDynamicConfig, expertAvailabilityWindowDays } from "../../settings";
import LoadingIndicator from '../UtilComponents/LoadingIndicator';
import { makeStyles, Theme, createStyles } from '@material-ui/core';
import { allSettled } from '../utils';
import StyledTooltip from '../CustomStyleComponents/Tooltip';
import { filterAndSortExperts } from '../../util/search';
import timanttiService from '../../services/timanttiService';
import bookingService from '../../services/bookingService';

interface SearchExpertProps {
    disabled?: boolean,
    selectedExperts: EntityResponse<Expert>[],
    setSelectedFilters: Dispatch<SetStateAction<SelectedFilters>>,
    expertFilters: string[],
    loadingInitialState: boolean,
    services: CampaignService[]
}

const useStyles = makeStyles((_theme: Theme) =>
    createStyles({
        loading: {
            width: "1.5em",
            height: "1.5em",
        }
    })
);

let arrowKeySelectionOn: boolean = false;

const SearchExpert: React.FC<SearchExpertProps> = props => {
    const {
        disabled,
        expertFilters,
        setSelectedFilters,
        selectedExperts,
        loadingInitialState,
    } = props;
    const { translation } = useContext(LocalizationContext);
    const [experts, setExperts] = useState<BasicExpert[]>([]);
    const [searchStr, setSearchStr] = useState<string>("");
    const [fetchedExperts, setFetchedExperts] = useState<BasicExpert[] | null>(null);
    const [selection, setSelection] = useState<BasicExpert[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const classes = useStyles();

    useEffect(() => {
        const { expertsTofilterOut } = getDynamicConfig();
        if (expertFilters.length !== 0 && !loadingInitialState) {
            const fetchExperts = async () => {
                const from = new Date();
                const to = new Date();
                to.setDate(to.getDate() + expertAvailabilityWindowDays);

                try {
                    const [resp, availableUuids] = await Promise.all([
                        timanttiService.getExperts(expertFilters),
                        bookingService.fetchExpertsWithOpenAppointments(from, to, expertFilters)
                            .catch(error => {
                                // Without availability information we show every expert rather than
                                // none. The list is then wider than intended, but still usable.
                                console.error("Could not resolve experts with open appointments.", error);
                                return null;
                            })
                    ]);

                    const available = availableUuids && new Set(availableUuids);

                    const filtered = resp.data.työntekijä
                        .filter(expert => !expertsTofilterOut.includes(expert.uuid))
                        .filter(expert => !available || available.has(expert.uuid));

                    setFetchedExperts(filtered);
                    setExperts(filtered
                        .map(expert => ({
                            uuid: expert.uuid,
                            Nimi: expert.Nimi
                        })));
                } catch (error) {
                    console.error("Could not fetch the experts for the expert filter.", error);
                    setFetchedExperts([]);
                    setExperts([]);
                } finally {
                    setLoading(false);
                }
            };
            fetchExperts();
        }
        if (expertFilters.length === 0) {
            setFetchedExperts(null);
        }
    }, [expertFilters, loadingInitialState]);

    useEffect(() => setLoading(true), [expertFilters]);

    useEffect(() => {
        if (selection) {
            const fetchSelectedExperts = async () => {
                const expertsToFetch = await allSettled(
                    selection.map(({ uuid }) => timanttiService.getExpert(uuid))
                );
                const resolvedExperts = expertsToFetch.reduce((acc, cur) => {
                    return cur.status === "fulfilled" ? [...acc, cur.value] : acc;
                }, [] as EntityResponse<Expert>[]);

                setSelectedFilters((prevState: SelectedFilters) => ({
                    ...prevState,
                    selectedExperts: resolvedExperts,
                }));
            };
            fetchSelectedExperts();
        }
    }, [selection]);

    const expertIsInFilterList = (expert: BasicExpert) =>
        expert && fetchedExperts?.map(expert => expert.uuid)?.includes(expert.uuid) && expert.Nimi;

    const nameMatchesSearchString = (expert: BasicExpert) =>
        expert && expert.Nimi &&
        expert.Nimi.toLowerCase().includes(searchStr.toLowerCase());

    const filteredExperts = experts
        .filter(expert =>
            fetchedExperts && fetchedExperts.length > 0 ?
                expertIsInFilterList(expert) :
                nameMatchesSearchString(expert)
        ).sort((expert1, expert2) =>
            expert1.Nimi.toLowerCase().localeCompare(expert2.Nimi.toLowerCase())
        );

    const onSelectionChange = (e: any, value: BasicExpert[]) => {
        setSelection(value);
        setSearchStr("");
    };

    const renderTags = (
        value: BasicExpert[],
        getTagProps: AutocompleteGetTagProps
    ) =>
        value.map((option, index) => <StyledTooltip
            key={`chip-tooltip-${option.uuid}`}
            title={option.Nimi}
        >
            <StyledChip
                key={`chip-${option.uuid}`}
                id={`chip-${option.uuid}`}
                label={option.Nimi} {...getTagProps({ index })}
            />
        </StyledTooltip>
        );

    const filterOptions = (entities: BasicExpert[]) => entities;

    const onInputChange = (e: any) => {
        e.preventDefault();
        const newValue = e.target.value;
        setSearchStr(newValue);
    };

    const filteredSearchExperts = useMemo(
        () => filterAndSortExperts(fetchedExperts, searchStr),
    [fetchedExperts, searchStr]);

    const selectedValues = useMemo(() => selectedExperts.map(expert => ({
        uuid: expert.uuid,
        Nimi: expert.data.Nimi
    })), [selectedExperts]);

    const loadingEl = (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
            {translation.loadingExperts}
            <LoadingIndicator className={classes.loading} />
        </div>
    );

    const onBlur = (e: any) => {
        e.preventDefault();
        setSearchStr("");
        arrowKeySelectionOn = false;
    };

    const handleKeyDown = (e: any) => {
        if (e.key === 'Enter') {
            if (arrowKeySelectionOn) {
                arrowKeySelectionOn = false;
                return;
            } else {
                const input = e.target.value.toLowerCase();
                if (input.length > 3) {
                    if(selection){
                        onSelectionChange(input, filteredSearchExperts.concat(selection));
                    } else {
                        onSelectionChange(input, filteredSearchExperts);
                    }
                }
            }
        } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            arrowKeySelectionOn = true;
        }
    };

    return (
        <MultiFilter<BasicExpert>
            disabled={disabled}
            id={"searchExpert"}
            options={loading ? [] : searchStr.length > 0 ? filteredSearchExperts : filteredExperts}
            label={translation.selectExpert}
            getOptionLabel={option => option.Nimi}
            renderTags={renderTags}
            placeholder={translation.searchExpert}
            onChange={onSelectionChange}
            value={selectedValues}
            filterOptions={filterOptions}
            onInputChange={onInputChange}
            loading={loading}
            loadingText={loadingEl}
            inputStr={searchStr}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
        />
    );
};

export default SearchExpert;
